import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { routeForPageId } from './menuData.js';
import HelpDrawer from '../help/HelpDrawer.jsx';
import { LanguagePicker, useLanguage } from '../i18n/LanguageContext.jsx';
import { roleLabel } from '../config/routeAccess.js';

export const SIGN_IN_PAGE_ID = 73933;

export function userInitials(user) {
  const src = (user && (user.name || user.email)) || '?';
  return src
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
}

export function useSignOut() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return () => {
    logout();
    navigate(routeForPageId(SIGN_IN_PAGE_ID));
  };
}

/** Session chrome: logged-in details + account menu + logout, or a login entry when anonymous. */
export default function AccountChrome({ variant = 'desktop' }) {
  const { user, isAuthenticated, loading } = useAuth();
  const signOut = useSignOut();
  const { t } = useLanguage();

  if (loading) return null;

  if (!isAuthenticated) {
    return (
      <div className="d-flex align-items-center gap-2">
        <HelpDrawer />
        <LanguagePicker id={`language-picker-anon-${variant}`} className="form-select form-select-sm w-auto" />
        <Link
          to={routeForPageId(SIGN_IN_PAGE_ID)}
          className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
        >
          <i className="fas fa-sign-in-alt me-1" aria-hidden="true"></i>
          {t('Log in')}
        </Link>
      </div>
    );
  }

  const role = t(roleLabel(user.role));
  const avatar = (
    <span
      className="rounded-circle border border-white border-2 d-inline-flex align-items-center justify-content-center bg-white text-primary fw-bold"
      style={{
        width: variant === 'mobile' ? 30 : 34,
        height: variant === 'mobile' ? 30 : 34,
        fontSize: 12,
      }}
      aria-hidden="true"
    >
      {userInitials(user)}
    </span>
  );

  return (
    <div className="d-flex align-items-center gap-2">
      <HelpDrawer />
      <div className="dropdown">
        <button
          className="btn btn-sm btn-outline-light dropdown-toggle d-flex align-items-center gap-2 border-0"
          type="button"
          id={`accountDropdown-${variant}`}
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-label={t('Account menu')}
        >
          {avatar}
          <span className="text-start d-none d-lg-block lh-sm">
            <span className="d-block fw-semibold small">{user.name || user.email}</span>
            <span className="d-block text-white-50 text-capitalize" style={{ fontSize: 11 }}>
              {role}
            </span>
          </span>
        </button>
        <ul
          className="dropdown-menu dropdown-menu-end shadow"
          aria-labelledby={`accountDropdown-${variant}`}
        >
          <li>
            <h6 className="dropdown-header small">
              <span className="d-block fw-bold text-dark">{user.name || user.email}</span>
              <span className="d-block text-muted">{user.email}</span>
            </h6>
          </li>
          <li>
            <span className="dropdown-item-text small text-muted text-capitalize">
              <i className="fas fa-user-tag me-2" aria-hidden="true"></i>
              {role}
            </span>
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li className="px-3 pb-2">
            <label htmlFor={`language-picker-${variant}`} className="form-label small text-muted mb-1">
              <i className="fas fa-language me-2" aria-hidden="true"></i>
              {t('Language')}
            </label>
            <LanguagePicker id={`language-picker-${variant}`} />
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li>
            <button type="button" className="dropdown-item text-danger" onClick={signOut}>
              <i className="fas fa-sign-out-alt me-2" aria-hidden="true"></i>
              {t('Sign out')}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}
